/**
 * Create Gateway Modal
 *
 * Modal para criar um novo Cloud Gateway.
 * Usa fluxo B2B2B: cliente seleciona produto gateway oferecido pelo reseller.
 */

import { useQuery, useMutation } from '@tanstack/react-query'
import {
  XIcon,
  RouterIcon,
  GlobeIcon,
  NetworkIcon,
  ShieldIcon,
  Loader2Icon,
  AlertTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
  CpuIcon,
  HardDriveIcon,
  MemoryStickIcon,
  DollarSignIcon,
  PackageIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import {
  gatewayApi,
  getMyGatewayProducts,
  getGatewayWanAvailableIPs,
  listCustomerVPCs,
} from '@nebula/shared/services'
import type { CloudGatewayCreate, GatewayProduct, GatewayWanMode } from '@nebula/shared/services'
import { getErrorMessage } from '@nebula/shared/utils'

type CreateGatewayModalProps = {
  onClose: () => void
  onCreated: () => void
}

export default function CreateGatewayModal({ onClose, onCreated }: CreateGatewayModalProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state - product_id é obrigatório
  const [formData, setFormData] = useState<
    Omit<CloudGatewayCreate, 'product_id'> & { product_id: string }
  >({
    name: '',
    product_id: '',
    // WAN. `wan_mode` é a fonte de verdade — `wan_dhcp` fica só por compat com
    // o contrato antigo da API.
    wan_mode: 'dhcp' as GatewayWanMode,
    wan_public_ip_id: null,
    wan_dhcp: true,
    wan_ip: '',
    wan_gateway: '',
    wan_netmask: '255.255.255.0',
    wan_dns: ['8.8.8.8', '8.8.4.4'],
    // LAN
    lan_vnet_id: '',
    lan_ip: '',
    lan_netmask: '255.255.255.0',
    lan_cidr: '',
    // Features
    nat_enabled: true,
    firewall_enabled: true,
    vpn_enabled: false,
    dhcp_server_enabled: false,
    // Specs
    cpu_cores: 2,
    memory_mb: 2048,
    storage_gb: 20,
  })

  // DHCP range state
  const [dhcpRangeStart, setDhcpRangeStart] = useState('')
  const [dhcpRangeEnd, setDhcpRangeEnd] = useState('')

  // Produto selecionado para exibir specs
  const [selectedProduct, setSelectedProduct] = useState<GatewayProduct | null>(null)

  // Get gateway products (via B2B2B hierarchy)
  const {
    data: productsData,
    isLoading: loadingProducts,
    error: productsError,
  } = useQuery({
    queryKey: ['my-gateway-products'],
    queryFn: getMyGatewayProducts,
  })

  // Get customer VPCs
  const { data: vpcsData, isLoading: loadingVpcs } = useQuery({
    queryKey: ['customer-vpcs'],
    queryFn: listCustomerVPCs,
  })

  // Auto-select when customer has only one VPC
  useEffect(() => {
    if (vpcsData?.vpcs?.length === 1 && !formData.lan_vnet_id) {
      const vpc = vpcsData.vpcs[0]
      setFormData((prev) => ({
        ...prev,
        lan_vnet_id: vpc.vnet_id,
        lan_cidr: vpc.cidr,
        lan_ip: vpc.gateway,
      }))
    }
  }, [vpcsData])

  // Update selected product and form specs when product_id changes
  useEffect(() => {
    if (formData.product_id && productsData?.products) {
      const product = productsData.products.find((p) => p.product_id === formData.product_id)
      setSelectedProduct(product || null)

      // Update specs from product
      if (product) {
        setFormData((prev) => ({
          ...prev,
          cpu_cores: product.specs.cpu_cores,
          memory_mb: Math.round(product.specs.memory_gb * 1024),
          storage_gb: product.specs.storage_gb,
          // Cluster sem DHCP na WAN: já entra em 'pool', porque manter 'dhcp'
          // aqui só produziria um gateway que provisiona e nunca conecta.
          ...(product.wan_dhcp_available === false && prev.wan_mode === 'dhcp'
            ? { wan_mode: 'pool' as GatewayWanMode, wan_dhcp: false }
            : {}),
        }))
      }
    } else {
      setSelectedProduct(null)
    }
  }, [formData.product_id, productsData])

  const dhcpDisponivel = selectedProduct?.wan_dhcp_available !== false

  // IPs públicos elegíveis no cluster do produto escolhido
  const {
    data: wanIpsData,
    isLoading: loadingWanIps,
    error: wanIpsError,
  } = useQuery({
    queryKey: ['gateway-wan-ips', selectedProduct?.cluster_id],
    queryFn: () => getGatewayWanAvailableIPs(selectedProduct!.cluster_id),
    enabled: !!selectedProduct?.cluster_id && formData.wan_mode === 'pool',
  })

  // Query que falha (403/404/500) deixa a lista vazia, e sem isto a tela
  // afirmaria "não há IP no pool" — mandando o cliente pedir ao provedor IPs
  // que existem. Mesmo tratamento do ChangeWanModal.
  const wanIpsErrorMsg = wanIpsError ? getErrorMessage(wanIpsError) : null

  const wanIps = wanIpsData?.ips ?? []
  const selectedWanIp = wanIps.find((ip) => ip.id === formData.wan_public_ip_id)

  const handleWanModeChange = (mode: GatewayWanMode) => {
    setFormData((prev) => ({
      ...prev,
      wan_mode: mode,
      wan_dhcp: mode === 'dhcp',
      // Cada modo carrega o próprio conjunto de campos; deixar resto preenchido
      // faz o backend recusar (ex.: 'pool' com wan_ip preenchido).
      wan_public_ip_id: mode === 'pool' ? prev.wan_public_ip_id : null,
      wan_ip: mode === 'static' ? prev.wan_ip : '',
      wan_gateway: mode === 'static' ? prev.wan_gateway : '',
      wan_netmask: mode === 'static' ? prev.wan_netmask || '255.255.255.0' : '',
    }))
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CloudGatewayCreate) => gatewayApi.createGateway(data),
    onSuccess: () => {
      onCreated()
    },
    onError: (err: any) => {
      setError(getErrorMessage(err))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.product_id) {
      setError('Selecione um produto gateway')
      return
    }

    if (!formData.lan_vnet_id) {
      setError('Selecione uma VPC para a rede LAN')
      return
    }

    // Validate WAN config
    if (formData.wan_mode === 'static') {
      if (!formData.wan_ip || !formData.wan_gateway) {
        setError('IP e Gateway são obrigatórios no endereçamento fixo')
        return
      }
      // Sem máscara o backend não tem como saber o tamanho do bloco — e um /24
      // presumido num bloco /25 deixa o gateway sem alcançar o next-hop.
      if (!formData.wan_netmask) {
        setError('Máscara de rede é obrigatória no endereçamento fixo')
        return
      }
    }

    if (formData.wan_mode === 'pool' && !loadingWanIps && wanIps.length === 0) {
      setError(
        wanIpsErrorMsg ??
          'Não há IP público disponível neste cluster. Solicite ao seu provedor o cadastro de novos IPs.',
      )
      return
    }

    // Submit com product_id obrigatório + DHCP range. Os campos de endereçamento
    // vão só no modo que os usa: o backend recusa combinações contraditórias.
    const submitData: CloudGatewayCreate = {
      ...formData,
      wan_ip: formData.wan_mode === 'static' ? formData.wan_ip : undefined,
      wan_gateway: formData.wan_mode === 'static' ? formData.wan_gateway : undefined,
      wan_netmask: formData.wan_mode === 'static' ? formData.wan_netmask : undefined,
      wan_public_ip_id: formData.wan_mode === 'pool' ? formData.wan_public_ip_id : undefined,
      ...(dhcpRangeStart && { dhcp_range_start: dhcpRangeStart }),
      ...(dhcpRangeEnd && { dhcp_range_end: dhcpRangeEnd }),
    }
    createMutation.mutate(submitData)
  }

  const handleChange = (field: keyof Omit<CloudGatewayCreate, 'cluster_id'>, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Format price for display
  const formatPrice = (price: number, currency: string, cycle: string) => {
    const formattedPrice = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(price)

    const cycleLabel =
      {
        hourly: '/hora',
        daily: '/dia',
        monthly: '/mês',
        annually: '/ano',
      }[cycle] || '/mês'

    return `${formattedPrice}${cycleLabel}`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="mobile-modal-surface flex w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <RouterIcon className="size-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Criar Cloud Gateway</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <XIcon className="size-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Error */}
          {error && (
            <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
              <AlertTriangleIcon className="size-5 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="flex items-center text-sm font-medium text-gray-700">
              <InfoIcon className="mr-2 size-4" />
              Informações Básicas
            </h3>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Nome do Gateway
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="gateway-minha-empresa"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
              />
              <p className="mt-1 text-xs text-gray-500">Deixe vazio para gerar automaticamente</p>
            </div>

            {/* Product Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                <PackageIcon className="mr-1 inline size-4" />
                Produto Gateway *
              </label>
              {loadingProducts ? (
                <div className="flex items-center space-x-2 rounded-lg bg-gray-50 p-3 text-gray-500">
                  <Loader2Icon className="size-4 animate-spin" />
                  <span className="text-sm">Carregando produtos disponíveis...</span>
                </div>
              ) : productsError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <div className="flex items-center space-x-2 text-red-700">
                    <AlertTriangleIcon className="size-4" />
                    <span className="text-sm">
                      Erro ao carregar produtos. Entre em contato com o suporte.
                    </span>
                  </div>
                </div>
              ) : productsData?.products && productsData.products.length > 0 ? (
                <>
                  <select
                    value={formData.product_id}
                    onChange={(e) => handleChange('product_id', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Selecione um produto gateway</option>
                    {productsData.products.map((product) => (
                      <option key={product.product_id} value={product.product_id}>
                        {product.first_vpc_free
                          ? `${product.product_name} - GRÁTIS`
                          : `${product.product_name} - ${formatPrice(product.base_price, product.currency, product.billing_cycle)}`}
                      </option>
                    ))}
                  </select>

                  {/* Show selected product info */}
                  {selectedProduct && (
                    <div className="space-y-3 rounded-lg border border-purple-100 bg-purple-50 p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-purple-800">
                          {selectedProduct.product_name}
                        </p>
                        {selectedProduct.first_vpc_free ? (
                          <span className="inline-flex items-center rounded-full bg-green-200 px-2 py-1 text-sm font-medium text-green-800">
                            Grátis
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-purple-200 px-2 py-1 text-sm font-medium text-purple-800">
                            <DollarSignIcon className="mr-1 size-3" />
                            {formatPrice(
                              selectedProduct.base_price,
                              selectedProduct.currency,
                              selectedProduct.billing_cycle,
                            )}
                          </span>
                        )}
                      </div>

                      {selectedProduct.product_description && (
                        <p className="text-xs text-purple-600">
                          {selectedProduct.product_description}
                        </p>
                      )}

                      <div className="grid grid-cols-1 gap-3 text-sm text-purple-700 sm:grid-cols-3">
                        <div className="flex items-center space-x-2">
                          <CpuIcon className="size-4" />
                          <span>{selectedProduct.specs.cpu_cores} vCPUs</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MemoryStickIcon className="size-4" />
                          <span>{selectedProduct.specs.memory_gb} GB</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <HardDriveIcon className="size-4" />
                          <span>{selectedProduct.specs.storage_gb} GB</span>
                        </div>
                      </div>

                      <div className="border-t border-purple-200 pt-2">
                        <p className="text-xs text-purple-500">
                          Template: {selectedProduct.template_name} • Cluster:{' '}
                          {selectedProduct.cluster_name}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedProduct?.first_vpc_free && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                      <p className="text-sm font-medium text-green-700">
                        Seu primeiro gateway é gratuito!
                      </p>
                      <p className="mt-1 text-xs text-green-600">
                        Este produto inclui o primeiro gateway sem custo.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                  <div className="flex items-center space-x-2 text-yellow-700">
                    <AlertTriangleIcon className="size-4" />
                    <span className="text-sm">
                      Nenhum produto gateway disponível. Entre em contato com seu provedor.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* WAN Configuration */}
          <div className="space-y-4">
            <h3 className="flex items-center text-sm font-medium text-gray-700">
              <GlobeIcon className="mr-2 size-4" />
              Configuração WAN (Internet)
            </h3>

            <div className="space-y-2">
              <label className="flex cursor-pointer items-start space-x-3 rounded-lg bg-gray-50 p-3 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">
                <input
                  type="radio"
                  name="wan_mode"
                  value="dhcp"
                  checked={formData.wan_mode === 'dhcp'}
                  disabled={!dhcpDisponivel}
                  onChange={() => handleWanModeChange('dhcp')}
                  className="mt-0.5 size-4 text-purple-600"
                />
                <span className="text-sm text-gray-700">
                  Obter IP automaticamente (DHCP)
                  {!dhcpDisponivel && (
                    <span className="mt-0.5 block text-xs text-amber-700">
                      Indisponível: a rede do provedor neste cluster não fornece DHCP
                    </span>
                  )}
                </span>
              </label>

              <label className="flex cursor-pointer items-start space-x-3 rounded-lg bg-gray-50 p-3">
                <input
                  type="radio"
                  name="wan_mode"
                  value="pool"
                  checked={formData.wan_mode === 'pool'}
                  onChange={() => handleWanModeChange('pool')}
                  className="mt-0.5 size-4 text-purple-600"
                />
                <span className="text-sm text-gray-700">
                  Usar um IP público do meu provedor
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Incluso no produto — endereço, máscara e DNS vêm configurados
                  </span>
                </span>
              </label>

              <label className="flex cursor-pointer items-start space-x-3 rounded-lg bg-gray-50 p-3">
                <input
                  type="radio"
                  name="wan_mode"
                  value="static"
                  checked={formData.wan_mode === 'static'}
                  onChange={() => handleWanModeChange('static')}
                  className="mt-0.5 size-4 text-purple-600"
                />
                <span className="text-sm text-gray-700">Informar endereçamento manualmente</span>
              </label>
            </div>

            {formData.wan_mode === 'pool' && (
              <div className="space-y-3">
                {loadingWanIps ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Loader2Icon className="size-4 animate-spin" />
                    <span>Carregando IPs disponíveis...</span>
                  </div>
                ) : wanIpsErrorMsg ? (
                  <div className="flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
                    <span>{wanIpsErrorMsg}</span>
                  </div>
                ) : wanIps.length === 0 ? (
                  <div className="flex items-start space-x-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                    <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
                    <span>
                      Nenhum IP público disponível neste cluster. Solicite ao seu provedor o
                      cadastro de novos IPs.
                    </span>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        IP público
                      </label>
                      <select
                        value={formData.wan_public_ip_id || ''}
                        onChange={(e) => handleChange('wan_public_ip_id', e.target.value || null)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Escolher automaticamente</option>
                        {wanIps.map((ip) => (
                          <option key={ip.id} value={ip.id}>
                            {ip.ip_address}
                            {ip.owned ? ' (já é seu)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedWanIp && (
                      <div className="space-y-0.5 rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                        <div>Gateway: {selectedWanIp.gateway || '—'}</div>
                        <div>Máscara: {selectedWanIp.netmask || '—'}</div>
                        <div>DNS: {selectedWanIp.dns_servers.join(', ') || '—'}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {formData.wan_mode === 'static' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">IP WAN *</label>
                  <input
                    type="text"
                    value={formData.wan_ip || ''}
                    onChange={(e) => handleChange('wan_ip', e.target.value)}
                    placeholder="203.0.113.10"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Gateway *</label>
                  <input
                    type="text"
                    value={formData.wan_gateway || ''}
                    onChange={(e) => handleChange('wan_gateway', e.target.value)}
                    placeholder="203.0.113.1"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Máscara de rede *
                  </label>
                  <input
                    type="text"
                    value={formData.wan_netmask || ''}
                    onChange={(e) => handleChange('wan_netmask', e.target.value)}
                    placeholder="255.255.255.0"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Servidores DNS
                  </label>
                  <input
                    type="text"
                    value={(formData.wan_dns || []).join(', ')}
                    onChange={(e) =>
                      handleChange(
                        'wan_dns',
                        e.target.value
                          .split(',')
                          .map((d) => d.trim())
                          .filter(Boolean),
                      )
                    }
                    placeholder="8.8.8.8, 8.8.4.4"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* LAN Configuration — VPC Selector */}
          <div className="space-y-4">
            <h3 className="flex items-center text-sm font-medium text-gray-700">
              <NetworkIcon className="mr-2 size-4" />
              Configuração LAN (Rede Interna)
            </h3>

            {/* VPC Select */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">VPC *</label>
              {loadingVpcs ? (
                <div className="flex items-center space-x-2 rounded-lg bg-gray-50 p-3 text-gray-500">
                  <Loader2Icon className="size-4 animate-spin" />
                  <span className="text-sm">Carregando VPCs...</span>
                </div>
              ) : vpcsData?.vpcs && vpcsData.vpcs.length > 0 ? (
                <select
                  value={formData.lan_vnet_id || ''}
                  onChange={(e) => {
                    const vnetId = e.target.value
                    const vpc = vpcsData.vpcs.find((v) => v.vnet_id === vnetId)
                    setFormData((prev) => ({
                      ...prev,
                      lan_vnet_id: vnetId,
                      lan_cidr: vpc?.cidr || '',
                      lan_ip: vpc?.gateway || '',
                    }))
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Selecione uma VPC</option>
                  {vpcsData.vpcs.map((vpc) => (
                    <option key={vpc.id} value={vpc.vnet_id}>
                      {vpc.name} — {vpc.cidr}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                  <div className="flex items-center space-x-2 text-yellow-700">
                    <AlertTriangleIcon className="size-4" />
                    <span className="text-sm">
                      Nenhuma VPC encontrada. Crie uma VPC antes de criar o gateway.
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* VPC Info + LAN IP */}
            {formData.lan_vnet_id &&
              (() => {
                const selectedVpc = vpcsData?.vpcs?.find((v) => v.vnet_id === formData.lan_vnet_id)
                return selectedVpc ? (
                  <div className="space-y-4">
                    {/* VPC Info Box */}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                      <div className="mb-2 flex items-center space-x-2">
                        <InfoIcon className="size-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          {selectedVpc.name}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-blue-700">
                        <span>VNI: {selectedVpc.vni}</span>
                        <span>CIDR: {selectedVpc.cidr}</span>
                        <span>Alocações: {selectedVpc.current_allocations}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          IP do Gateway na LAN
                        </label>
                        <input
                          type="text"
                          value={formData.lan_ip || ''}
                          onChange={(e) => handleChange('lan_ip', e.target.value)}
                          placeholder={selectedVpc.gateway}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Deve pertencer ao CIDR {selectedVpc.cidr}
                        </p>
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Rede (CIDR)
                        </label>
                        <input
                          type="text"
                          value={formData.lan_cidr || ''}
                          readOnly
                          className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                ) : null
              })()}
          </div>

          {/* Features */}
          <div className="space-y-4">
            <h3 className="flex items-center text-sm font-medium text-gray-700">
              <ShieldIcon className="mr-2 size-4" />
              Funcionalidades
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex cursor-pointer items-center space-x-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={formData.nat_enabled}
                  onChange={(e) => handleChange('nat_enabled', e.target.checked)}
                  className="size-4 rounded text-purple-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">NAT/Masquerade</p>
                  <p className="text-xs text-gray-500">Tradução de endereços</p>
                </div>
              </label>

              <label className="flex cursor-pointer items-center space-x-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={formData.firewall_enabled}
                  onChange={(e) => handleChange('firewall_enabled', e.target.checked)}
                  className="size-4 rounded text-purple-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Firewall</p>
                  <p className="text-xs text-gray-500">Proteção de perímetro</p>
                </div>
              </label>

              <label className="flex cursor-pointer items-center space-x-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={formData.vpn_enabled}
                  onChange={(e) => handleChange('vpn_enabled', e.target.checked)}
                  className="size-4 rounded text-purple-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">VPN</p>
                  <p className="text-xs text-gray-500">WireGuard (em breve)</p>
                </div>
              </label>

              <label className="flex cursor-pointer items-center space-x-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={formData.dhcp_server_enabled}
                  onChange={(e) => handleChange('dhcp_server_enabled', e.target.checked)}
                  className="size-4 rounded text-purple-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">DHCP Server</p>
                  <p className="text-xs text-gray-500">Servidor DHCP na LAN</p>
                </div>
              </label>
            </div>

            {/* DHCP Range Fields - shown when DHCP is enabled */}
            {formData.dhcp_server_enabled && (
              <div className="mt-4 space-y-3 rounded-lg border border-green-200 bg-green-50 p-4">
                <div className="flex items-center space-x-2">
                  <InfoIcon className="size-4 text-green-600" />
                  <p className="text-sm font-medium text-green-800">Faixa DHCP</p>
                </div>
                <p className="text-xs text-green-600">
                  Configure a faixa de IPs que serão distribuídos automaticamente via DHCP. Você
                  pode ajustar isso depois em Networking &gt; IPs.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      IP Inicial
                    </label>
                    <input
                      type="text"
                      value={dhcpRangeStart}
                      onChange={(e) => setDhcpRangeStart(e.target.value)}
                      placeholder={(() => {
                        const base =
                          formData.lan_cidr?.split('/')[0]?.split('.').slice(0, 3).join('.') ||
                          '10.0.0'
                        return `${base}.100`
                      })()}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">IP Final</label>
                    <input
                      type="text"
                      value={dhcpRangeEnd}
                      onChange={(e) => setDhcpRangeEnd(e.target.value)}
                      placeholder={(() => {
                        const base =
                          formData.lan_cidr?.split('/')[0]?.split('.').slice(0, 3).join('.') ||
                          '10.0.0'
                        return `${base}.200`
                      })()}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Advanced Options */}
          <div className="rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50"
            >
              <span className="text-sm font-medium text-gray-700">Opções Avançadas</span>
              {showAdvanced ? (
                <ChevronUpIcon className="size-4 text-gray-400" />
              ) : (
                <ChevronDownIcon className="size-4 text-gray-400" />
              )}
            </button>

            {showAdvanced && (
              <div className="space-y-4 border-t border-gray-200 p-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <InfoIcon className="size-4" />
                  <span>
                    As especificações são definidas pelo produto selecionado. Para alterar, escolha
                    outro produto.
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">CPU (cores)</label>
                    <input
                      type="text"
                      value={formData.cpu_cores}
                      readOnly
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">Memória</label>
                    <input
                      type="text"
                      value={`${((formData.memory_mb ?? 0) / 1024).toFixed(1)} GB`}
                      readOnly
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">Storage</label>
                    <input
                      type="text"
                      value={`${formData.storage_gb} GB`}
                      readOnly
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-6 py-4 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              createMutation.isPending ||
              !formData.product_id ||
              !formData.lan_vnet_id ||
              loadingProducts ||
              !!productsError
            }
            className="inline-flex w-full items-center justify-center rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {createMutation.isPending ? (
              <>
                <Loader2Icon className="mr-2 size-4 animate-spin" />
                Criando e Provisionando...
              </>
            ) : (
              <>
                <RouterIcon className="mr-2 size-4" />
                Criar e Provisionar Gateway
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
